import { LDFlagSet } from "launchdarkly-js-client-sdk";
import { Location } from "history";
import {
  AppState,
  Envs,
  LowIncomeSubsidyStatus,
  PlanType,
  UserLanguage,
  UserRole,
} from "../@types";
import routes from "../app/routes";
import { getEnvironment, hasLoginSession } from "./loginHelpers";
import { isBeneNewToMedicare } from "./objectUtilities";
import { getRouteName } from "./routeHelpers";
import { isOutsideOpenEnrollment } from "./enrollmentPhaseHelpers";

export interface TealiumEventProps {
  event_action: string;
  event_label: string;
  additional_props?: Record<string, unknown>;
}

export enum Ga4Event {
  ADD_PROVIDER_TO_COMPARE = "mct_provider_added_for_comparison",
  COMPARE_PROVIDERS = "mct_providers_compared",
  CONFIRM_PHARMACIES_VIEWED = "mct: confirm pharmacies",
  COVERAGE_SELECTED = "mct_coverage_type_selected",
  CW_COMPLETED = "mct_coverage_wizard_completed",
  CW_STARTED = "mct_coverage_wizard_started",
  DRUG_ADDED = "mct_plan_finder_drug_added",
  DRUG_EDITED = "mct_plan_finder_drug_edited",
  DRUG_REMOVED = "mct_plan_finder_drug_removed",
  DRUG_SEARCH_PREF = "mct_plan_finder_drug_search_preferences",
  DRUGS_MANAGED = "mct_plan_finder_drugs_managed",
  ENROLLMENT_COMPLETED = "enrollment_completed",
  ENROLLMENT_STARTED = "enrollment_started",
  FILTER_CLEARED = "filter_cleared",
  HELP_DRAWER_CLOSED = "help_drawer_closed",
  HELP_DRAWER_OPENED = "help_drawer_opened",
  IN_NETWORK_PHARMACY_BACK_TO_PLAN_DETAILS = "mct_back_to_plan_details_clicked",
  IN_NETWORK_PHARMACY_SELECTED = "mct_plan_finder_pharmacy_selected",
  JUMP_LINK_CLICKED = "jump_link_clicked",
  LIST_SORTED = "list_sorted",
  PACE_PLANS_CTA_CLICKED = "mct_pace_plans_cta_clicked",
  PHARMACY_REMOVED = "mct_plan_finder_pharmacy_removed",
  PHARMACY_SEARCH_RESULTS = "mct_plan_finder_pharmacy_search_results",
  PHARMACY_SELECTED = "mct_plan_finder_pharmacies_managed",
  PHARMACY_SELECTION = "mct_plan_finder_pharmacy_selection",
  PLAN_DETAILS = "mct_plan_finder_plan_details",
  PLAN_DETAILS_COVERAGE_GAP = "plan_details_subtable_header_caret_when_will_you_enter_the_coverage_gap",
  PLAN_DETAILS_HELP_CARET_CLICKED = "mct_plan_details_help_caret_clicked",
  PLAN_DETAILS_NAVIGATION_CLICKED = "mct_plan_details_navigation_tab_clicked",
  PLAN_DETAILS_VIEW_MORE_CLOSED = "mct_plan_details_view_more_closed",
  PLAN_DETAILS_VIEW_MORE_EXPANDED = "mct_plan_details_view_more_expanded",
  PLAN_ENROLL_CLICKED = "mct_plan_finder_plan_enroll_clicked",
  PLAN_FINDER_ADD_RECENT_FILLED_DRUGS = "mct_plan_finder_add_recent_filled_drugs",
  PLAN_FINDER_COMPLETED = "mct_plan_finder_completed",
  PLAN_FINDER_EDIT_DRUG = "mct_plan_finder_edit_drug",
  PLAN_FINDER_PHARMACY_REMOVED = "mct_plan_finder_pharmacy_removed",
  PLAN_FINDER_PHARMACY_SEARCH_RESULTS = "mct_plan_finder_pharmacy_search_results",
  PLAN_FINDER_SAVED_DRUGS_MANAGED = "mct_plan_finder_saved_drugs_managed",
  PLAN_FINDER_SAVED_PHARMACIES_MANAGED = "mct_plan_finder_saved_pharmacies_managed",
  PLAN_FINDER_STARTED = "mct_plan_finder_started",
  PRINT = "print_page",
  RADIO_BUTTON_SELECTED = "radio_button_list_selected",
  RESULTS_FILTERED = "results_filtered",
  SAVED_PHARMACIES_VIEWED = "mct_plan_finder_saved_pharmacies_viewed",
  VIEW_DRUG_COVERAGE = "mct_plan_finder_view_drug_coverage",
  VIEW_DRUG_LIST = "mct_plan_finder_view_drug_list",
}

export enum Ga4EventDimension {
  CLICK_TEXT = "click_text",
  COMPARE_COUNT = "compare_count",
  CONTENT_LANGUAGE = "content_language",
  CONTENT_TYPE = "content_type",
  COVERAGE_TYPE = "coverage_type",
  DRUG_COUNT = "drug_count",
  DRUG_NAME = "drug_name",
  DRUG_TYPE = "drug_type",
  EVENT_NAME = "event_name",
  FILTER_ACTION = "filter_action",
  HEADER = "header",
  HEADING = "heading",
  LINK_URL = "link_url",
  LOGGED_IN = "logged_in",
  MCT_COVERAGE_WIZARD = "mct_coverage_wizard",
  MCT_IN_NETWORK_PHARMACY_FINDER = "mct_in_network_pharmacy_finder",
  MCT_INSURANCE_PROVIDER = "mct_insurance_provider",
  MCT_INSURANCE_TYPE = "mct_insurance_type",
  MCT_LIS = "mct_lis",
  MCT_MEDIGAP = "mct_medigap",
  MCT_PHARMACY_NAME = "mct_pharmacy_name",
  MCT_PHARMACY_TYPE = "mct_pharmacy_type",
  MCT_PLAN_TYPE = "mct_plan_type",
  MCT_VIEWED_DRUGS = "mct_viewed_drugs",
  OPEN_ENROLLMENT = "open_enrollment",
  PAGE_NAME = "page_name",
  PHARMACY_COUNT = "pharmacy_count",
  POSITION = "position",
  RADIO_BUTTON_TEXT = "radio_button_text",
  ROLE = "role",
  SEARCH_RESULT_COUNT = "search_result_count",
  SEARCH_RESULT_POSITION = "search_result_position",
  SITE_DOMAIN = "site_domain",
  SITE_ENVIRONMENT = "site_environment",
  SITE_SECTION = "site_section",
  STAR_RATING = "star_rating",
  SUB_SECTION = "sub_section",
  TEXT = "text",
}

export const Ga4EventDimensionsConfig: {
  [key: string]: Ga4EventDimension[];
} = {
  [Ga4Event.ENROLLMENT_COMPLETED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_INSURANCE_PROVIDER,
    Ga4EventDimension.MCT_INSURANCE_TYPE,
  ],
  [Ga4Event.ENROLLMENT_STARTED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_INSURANCE_PROVIDER,
    Ga4EventDimension.MCT_INSURANCE_TYPE,
  ],
  [Ga4Event.CW_COMPLETED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
  ],
  [Ga4Event.CW_STARTED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
  ],
  [Ga4Event.PLAN_FINDER_COMPLETED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_INSURANCE_PROVIDER,
    Ga4EventDimension.MCT_INSURANCE_TYPE,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.MCT_PLAN_TYPE,
    Ga4EventDimension.MCT_VIEWED_DRUGS,
  ],
  [Ga4Event.DRUG_SEARCH_PREF]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PLAN_TYPE,
  ],
  [Ga4Event.DRUGS_MANAGED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PLAN_TYPE,
  ],
  [Ga4Event.PHARMACY_SELECTION]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PLAN_TYPE,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.MCT_PHARMACY_NAME,
    Ga4EventDimension.SEARCH_RESULT_COUNT,
    Ga4EventDimension.SEARCH_RESULT_POSITION,
  ],
  [Ga4Event.PHARMACY_SELECTED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PLAN_TYPE,
    Ga4EventDimension.CLICK_TEXT,
    Ga4EventDimension.PHARMACY_COUNT,
    Ga4EventDimension.TEXT,
  ],
  [Ga4Event.PLAN_DETAILS]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PLAN_TYPE,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.MCT_VIEWED_DRUGS,
  ],
  [Ga4Event.PLAN_DETAILS_NAVIGATION_CLICKED]: [Ga4EventDimension.CLICK_TEXT],
  [Ga4Event.PLAN_DETAILS_HELP_CARET_CLICKED]: [
    Ga4EventDimension.HEADER,
    Ga4EventDimension.SUB_SECTION,
  ],
  [Ga4Event.PLAN_DETAILS_VIEW_MORE_EXPANDED]: [Ga4EventDimension.CLICK_TEXT],
  [Ga4Event.PLAN_DETAILS_VIEW_MORE_CLOSED]: [Ga4EventDimension.CLICK_TEXT],
  [Ga4Event.PLAN_ENROLL_CLICKED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.MCT_INSURANCE_PROVIDER,
    Ga4EventDimension.MCT_INSURANCE_TYPE,
    Ga4EventDimension.MCT_LIS,
    Ga4EventDimension.MCT_MEDIGAP,
    Ga4EventDimension.MCT_PLAN_TYPE,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.MCT_VIEWED_DRUGS,
    Ga4EventDimension.CONTENT_TYPE,
  ],
  [Ga4Event.PLAN_FINDER_STARTED]: [
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.MCT_COVERAGE_WIZARD,
    Ga4EventDimension.TEXT,
    Ga4EventDimension.EVENT_NAME,
  ],
  [Ga4Event.FILTER_CLEARED]: [Ga4EventDimension.TEXT],
  [Ga4Event.RESULTS_FILTERED]: [
    Ga4EventDimension.TEXT,
    Ga4EventDimension.CLICK_TEXT,
    Ga4EventDimension.RADIO_BUTTON_TEXT,
    Ga4EventDimension.FILTER_ACTION,
  ],
  [Ga4Event.JUMP_LINK_CLICKED]: [Ga4EventDimension.TEXT],
  [Ga4Event.VIEW_DRUG_LIST]: [Ga4EventDimension.TEXT],
  [Ga4Event.VIEW_DRUG_COVERAGE]: [Ga4EventDimension.TEXT],
  [Ga4Event.LIST_SORTED]: [Ga4EventDimension.TEXT],
  [Ga4Event.COVERAGE_SELECTED]: [Ga4EventDimension.COVERAGE_TYPE],
  [Ga4Event.DRUG_ADDED]: [Ga4EventDimension.DRUG_TYPE],
  [Ga4Event.DRUG_EDITED]: [Ga4EventDimension.DRUG_TYPE],
  [Ga4Event.DRUG_REMOVED]: [
    Ga4EventDimension.DRUG_NAME,
    Ga4EventDimension.DRUG_TYPE,
    Ga4EventDimension.TEXT,
  ],
  [Ga4Event.PHARMACY_REMOVED]: [
    Ga4EventDimension.MCT_PHARMACY_NAME,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.MCT_IN_NETWORK_PHARMACY_FINDER,
    Ga4EventDimension.PHARMACY_COUNT,
  ],
  [Ga4Event.IN_NETWORK_PHARMACY_SELECTED]: [
    Ga4EventDimension.MCT_PHARMACY_NAME,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.MCT_IN_NETWORK_PHARMACY_FINDER,
    Ga4EventDimension.PHARMACY_COUNT,
  ],
  [Ga4Event.ADD_PROVIDER_TO_COMPARE]: [
    Ga4EventDimension.SEARCH_RESULT_COUNT,
    Ga4EventDimension.SEARCH_RESULT_POSITION,
    Ga4EventDimension.MCT_INSURANCE_PROVIDER,
    Ga4EventDimension.MCT_INSURANCE_TYPE,
    Ga4EventDimension.STAR_RATING,
  ],
  [Ga4Event.COMPARE_PROVIDERS]: [Ga4EventDimension.COMPARE_COUNT],
  [Ga4Event.PRINT]: [],
  [Ga4Event.RADIO_BUTTON_SELECTED]: [
    Ga4EventDimension.TEXT,
    Ga4EventDimension.HEADING,
    Ga4EventDimension.POSITION,
  ],
  [Ga4Event.IN_NETWORK_PHARMACY_BACK_TO_PLAN_DETAILS]: [
    Ga4EventDimension.CLICK_TEXT,
  ],
  [Ga4Event.HELP_DRAWER_OPENED]: [Ga4EventDimension.CLICK_TEXT],
  [Ga4Event.HELP_DRAWER_CLOSED]: [Ga4EventDimension.CLICK_TEXT],
  [Ga4Event.PHARMACY_SEARCH_RESULTS]: [Ga4EventDimension.CLICK_TEXT],
  [Ga4Event.SAVED_PHARMACIES_VIEWED]: [Ga4EventDimension.EVENT_NAME],
  [Ga4Event.PLAN_FINDER_PHARMACY_SEARCH_RESULTS]: [
    Ga4EventDimension.TEXT,
    Ga4EventDimension.EVENT_NAME,
    Ga4EventDimension.PHARMACY_COUNT,
  ],
  [Ga4Event.PLAN_FINDER_SAVED_PHARMACIES_MANAGED]: [
    Ga4EventDimension.TEXT,
    Ga4EventDimension.EVENT_NAME,
  ],
  [Ga4Event.PLAN_FINDER_PHARMACY_REMOVED]: [
    Ga4EventDimension.EVENT_NAME,
    Ga4EventDimension.PHARMACY_COUNT,
    Ga4EventDimension.MCT_PHARMACY_NAME,
    Ga4EventDimension.MCT_PHARMACY_TYPE,
    Ga4EventDimension.TEXT,
  ],
  [Ga4Event.PACE_PLANS_CTA_CLICKED]: [
    Ga4EventDimension.CONTENT_LANGUAGE,
    Ga4EventDimension.CONTENT_TYPE,
    Ga4EventDimension.LINK_URL,
    Ga4EventDimension.LOGGED_IN,
    Ga4EventDimension.OPEN_ENROLLMENT,
    Ga4EventDimension.PAGE_NAME,
    Ga4EventDimension.ROLE,
    Ga4EventDimension.SITE_DOMAIN,
    Ga4EventDimension.SITE_ENVIRONMENT,
    Ga4EventDimension.SITE_SECTION,
    Ga4EventDimension.TEXT,
  ],
  [Ga4Event.PLAN_FINDER_ADD_RECENT_FILLED_DRUGS]: [Ga4EventDimension.TEXT],
  [Ga4Event.PLAN_FINDER_SAVED_DRUGS_MANAGED]: [
    Ga4EventDimension.DRUG_COUNT,
    Ga4EventDimension.TEXT,
  ],
  [Ga4Event.PLAN_FINDER_EDIT_DRUG]: [
    Ga4EventDimension.DRUG_NAME,
    Ga4EventDimension.DRUG_TYPE,
    Ga4EventDimension.TEXT,
  ],
};

export interface Ga4EventDimensions {
  click_text?: string;
  compare_count?: number;
  content_type?: string;
  coverage_type?: string;
  drug_count?: number;
  drug_name?: string;
  drug_type?: string;
  event_name: Ga4Event | string;
  filter_action?: "applied" | "removed";
  flags: LDFlagSet;
  header?: string;
  heading?: string;
  link_url?: string;
  logged_in?: boolean;
  mct_coverage_wizard?: boolean;
  mct_in_network_pharmacy_finder?: "yes" | "no";
  mct_insurance_provider?: string;
  mct_insurance_type?: PlanType;
  mct_lis?: LowIncomeSubsidyStatus;
  mct_medigap?: boolean;
  mct_pharmacy_name?: string;
  mct_pharmacy_type?: string;
  mct_plan_type?: PlanType;
  mct_viewed_drugs?: boolean;
  open_enrollment?: boolean;
  page_name?: string;
  pharmacy_count?: number;
  radio_button_text?: string;
  role?: UserRole;
  search_result_count?: number;
  search_result_position?: number;
  site_domain?: string;
  site_environment?: string;
  site_section?: string;
  star_rating?: number;
  state: AppState;
  sub_section?: string;
  text?: string;
}

interface Ga4PageViewDimensions {
  [key: string]: string;
  content_language: UserLanguage;
  content_type: string;
  logged_in: "true" | "false";
  open_enrollment: "true" | "false";
  page_name: string;
  role: UserRole;
  site_domain: string;
  site_environment: Envs;
  site_section: string;
}

export function createGa4Event(
  settings: Ga4EventDimensions
): Omit<Ga4EventDimensions, "flags" | "state"> {
  const { event_name, flags, state } = settings;
  const {
    beneficiary,
    csr,
    cwCoverage,
    language,
    lis,
    planType,
    prescriptions,
    selectedPlan,
    viewMedigap,
  } = state;
  const {
    mctYpOeNextYear,
    mctYpOpenEnrollment,
    mctYpOutsideOpenEnrollment,
    mctYpPublicPreview,
  } = flags;
  const logged_in = hasLoginSession() && !!beneficiary;
  const open_enrollment = !isOutsideOpenEnrollment({
    mctYpOeNextYear,
    mctYpOpenEnrollment,
    mctYpOutsideOpenEnrollment,
    mctYpPublicPreview,
  });

  const dimensionValueMapping = {
    [Ga4EventDimension.CONTENT_LANGUAGE]: language,
    [Ga4EventDimension.LOGGED_IN]: logged_in,
    [Ga4EventDimension.MCT_COVERAGE_WIZARD]: !!cwCoverage,
    [Ga4EventDimension.MCT_INSURANCE_PROVIDER]:
      settings?.mct_insurance_provider || selectedPlan?.name,
    [Ga4EventDimension.MCT_INSURANCE_TYPE]:
      settings?.mct_insurance_type || selectedPlan?.plan_type,
    [Ga4EventDimension.MCT_LIS]: lis,
    [Ga4EventDimension.MCT_MEDIGAP]: viewMedigap,
    [Ga4EventDimension.MCT_PHARMACY_TYPE]:
      settings?.mct_pharmacy_type !== undefined
        ? settings.mct_pharmacy_type
        : state.pharmacyType,
    [Ga4EventDimension.MCT_PLAN_TYPE]: planType,
    [Ga4EventDimension.MCT_VIEWED_DRUGS]: prescriptions.length > 0,
    [Ga4EventDimension.OPEN_ENROLLMENT]: open_enrollment,
    [Ga4EventDimension.ROLE]: (() => {
      let role = UserRole.ANONYMOUS;
      if (csr) {
        role = UserRole.CSR;
      } else if (beneficiary) {
        role = UserRole.BENEFICIARY;
      }
      return role;
    })(),
    [Ga4EventDimension.SITE_ENVIRONMENT]: getEnvironment(),
    [Ga4EventDimension.SITE_DOMAIN]: window.location.hostname,
  };

  const eventDimensions = Ga4EventDimensionsConfig[event_name] || [];

  const dimensions = { event_name };

  eventDimensions.forEach(k => {
    const mappedValue = dimensionValueMapping[k];
    const settingsValue = settings[k];

    if (mappedValue !== undefined) {
      dimensions[k] = mappedValue;
    } else if (settingsValue !== undefined) {
      dimensions[k] = settingsValue;
    }
  });

  window.utag?.link(dimensions);
  return dimensions;
}

export async function sendTealiumEvent(
  event_action: string,
  event_label: string,
  other_props = {}
): Promise<string> {
  if (!window.utag) return "";
  const event_category = "MCT";

  window.utag.link({
    event_category,
    event_action,
    event_label,
    event_noninteraction: 0,
    ...other_props,
  });

  return `Tealium event sent: ${event_category} - ${event_action} - ${event_label}`;
}

/**
 * Used on CSR Landing page only, otherwise use sendTealiumEvent
 */
export async function sendTealiumLink(
  props: Record<string, unknown>
): Promise<string> {
  if (!window.utag) return "";
  window.utag.link(props);
  return "Tealium link sent";
}

function createGa4PageViewPayload({
  state,
  location: routerLocation,
  flags,
}: {
  state: AppState;
  location: Location;
  flags: LDFlagSet;
}): Ga4PageViewDimensions {
  const { beneficiary, csr, language } = state;
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = flags;
  const site_domain = window.location.host;
  const site_environment = getEnvironment();
  const site_section = "mct";
  const content_type = "mct page";
  const content_language = language;
  const logged_in = hasLoginSession() && !!beneficiary ? "true" : "false";
  const page_name = getRouteName(routerLocation.pathname);
  const open_enrollment = !isOutsideOpenEnrollment({
    mctYpOeNextYear,
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOutsideOpenEnrollment,
  })
    ? "true"
    : "false";

  let role = UserRole.ANONYMOUS;

  if (csr) {
    role = UserRole.CSR;
  } else if (beneficiary) {
    role = UserRole.BENEFICIARY;
  }

  return {
    content_language,
    content_type,
    logged_in,
    open_enrollment,
    page_name,
    role,
    site_domain,
    site_environment,
    site_section,
  };
}

function createTealiumPageViewPayload({
  mctCurrentPlanYear,
  page_name,
  page_type = "application",
  state: appState,
}: {
  mctCurrentPlanYear: number | undefined;
  page_name: string;
  page_type: string;
  state: AppState;
}): Record<string, unknown> {
  const page = `${window.location.pathname}${window.location.hash.replace(
    /\?.+/,
    ""
  )}`;
  let lis_level = appState.lis || "";
  if (
    appState.futureLis &&
    mctCurrentPlanYear &&
    appState.year !== mctCurrentPlanYear.toString()
  ) {
    lis_level = appState.futureLis;
  }

  return {
    page,
    page_name,
    page_type,
    count_insurance_carriers: appState.searchResultsFilters.insuranceCarriers.length.toString(),
    count_pharmacies: appState.pharmacies.length.toString(),
    count_prescriptions: appState.prescriptions.length.toString(),
    insurance_carriers: appState.searchResultsFilters.insuranceCarriers.join(
      "|"
    ),
    lis_level,
    language: window.location.hash.includes("lang=es")
      ? "es"
      : appState.language,
    pharmacy_type: appState.pharmacyType,
    plan_type: appState.planType || "",
    prescriptions_ndc: appState.prescriptions.map(p => p.ndc).join("|"),
    logged_in: appState.beneficiary ? "true" : "false",
    user_type: appState.csr
      ? "csr"
      : appState.beneficiary
      ? "beneficiary"
      : "guest",
    view_coverage_wizard: appState.cwCoverage ? "true" : "false",
    new_to_medicare_beneficiary: isBeneNewToMedicare(appState),
  };
}

const shouldSendTealiumView = () => {
  const page = `${window.location.pathname}${window.location.hash.replace(
    /\?.+/,
    ""
  )}`;
  return (
    !page.includes(routes.slsCallback) && !page.includes(routes.mbpLandingPage)
  );
};

/**
 * Sends a virtual page view for UA, only
 */
export function sendTealiumView(
  page_name: string,
  appState: AppState,
  mctCurrentPlanYear: number | undefined,
  page_type = "application"
): void {
  if (!window.utag?.view || !shouldSendTealiumView()) {
    return;
  }

  window.utag.view(
    createTealiumPageViewPayload({
      page_name,
      state: appState,
      mctCurrentPlanYear,
      page_type,
    })
  );
}

/**
 * Creates a virtual page view for GA4 and, if appropriiate for the current
 * route/location, UA. Called from the `ReferrerDetector` component to handle
 * triggering a view on every route change
 */
export function createUTagPageView({
  flags,
  location: routerLocation,
  mctCurrentPlanYear,
  page_name,
  page_type = "application",
  state,
}: {
  flags: LDFlagSet;
  location: Location;
  mctCurrentPlanYear: number | undefined;
  page_name: string;
  page_type?: string;
  state: AppState;
}): void {
  if (!window.utag?.view) {
    return;
  }
  let payload:
    | Ga4PageViewDimensions
    | Record<string, unknown> = createGa4PageViewPayload({
    state,
    location: routerLocation,
    flags,
  });
  if (shouldSendTealiumView()) {
    payload = {
      ...createTealiumPageViewPayload({
        page_name,
        state,
        mctCurrentPlanYear,
        page_type,
      }),
      ...payload,
    };
  }
  window.utag.view(payload);
}
